import { get } from 'lodash';
import screenfull from 'screenfull';
import createInteractiveListeners from 'components/Interactive/listeners';
import normalizeEventName from './normalizeEventName';
import {
  calculateDerivedProps,
  registerDerivedProp,
} from './derivedPropsProcessing';
import { BROWSER_AUTOPLAY_STATE } from '../index';
import { getTypeOfContent } from './getTypeOfContent';

const initialState = {
  videoId: null,
  currentTime: 0,
  duration: 0,
  channelState: null,
  programDuration: 0,
  programPosition: 0,
  currentProgramTime: 0,
  loadedFraction: 0,
  volume: 0,
  paused: false,
  isBuffering: false,
  started: false,
  ended: false,
  muted: false,
  thumbnails: [],
  type: '',
  isWatermarkClickable: false,
  srcWaterMark: '',
  shareLink: '',
  contentType: '',
  title: '',
  isFullscreen: false,
  isMobile: false,
  isAndroid: false,
  qualityList: [],
  quality: '',
  audioTrackList: [],
  audioTrack: '',
  subtitlesList: [],
  subtitles: '',
  subtitlesText: '',
  isAutoplay: true,
  isLoadCalled: false,
  isFavorite: false,
  isFavoritePhrase: '',
  isHybridAdSkin: false,
  messageModeData: false,
  chromecastState: 'notAvailable',
  channel: undefined,
  smallMode: false,
  showMainLoader: false,
  disableLoaders: false,
  mainContentStarted: false,
  isPipModeOn: false,
  pipIsSupported: false,
  showSkipIntroButton: false,
  skipIntroSkipped: false,
  showFavoriteButton: true,
  showShareButton: false,
  showVolumeButton: false,
  interactiveElements: [],
  isInteractiveFullscreen: false,
  // this flag controls the skin's hiding by mouse interaction timeout
  isAbleToHideSkin: true,
  cmsWantsToHideSkin: false,
  playlistInfo: null,
  playlistItem: null,
  epgCurrentTimestamp: 0,
  liveDuration: 0,
  livePosition: 0,
};

export default (player, onUpdate, getState) => {
  let pauseTimeoutIdOn5State = undefined;

  const state = {
    ...initialState,
  };

  const updateOnEvent = (sliceName) => (event) => {
    player.utils.log(`update ${sliceName} on ${event.name}`, event.data);
    const patch = {
      [sliceName]: event.data,
    };
    onUpdate(patch);
  };

  player.on('property:videoInfo', (event) => {
    window.setTimeout(listen, 0, event);
  });
  player.on('property:liveDvr:position', listen);
  player.on('property:liveDvr:duration', listen);
  player.on('property:liveDvr:time', listen);
  player.on('property:liveDvr:isTimeshift', listen);
  player.on('property:position', listen);
  player.on('property:duration', listen);
  player.on('property:videoInstances', listen);
  player.on('property:muted', listen);
  player.on('property:volume', listen);
  player.on('property:qualityList', listen);
  player.on('property:quality', listen);
  player.on('property:audioTrackList', listen);
  player.on('property:audioTrack', listen);
  player.on('property:subtitlesList', listen);
  player.on('property:subtitles', listen);
  player.on('property:thumbnails', listen);
  player.on('player:stateChange', listen);
  player.on('ad:vendor:stateChange', listen);
  player.on('property:subtitlesTextChange', listen);
  player.on('property:player:isFavorite', listen);
  player.on('property:chromecastState', listen);
  player.on('property:backgroundPoster', listen);
  player.on('property:autoplay', listen);
  player.on('property:loadCalled', listen);
  player.on('property:loadedFraction', listen);
  player.on('property:interactiveChannel:currentPlayingProgram', listen);
  player.on('property:interactiveChannel:currentProgramDuration', listen);
  player.on('property:interactiveChannel:currentPlayingTime', listen);
  player.on('player:messageMode', listen);
  player.on('property:epg:currentProgramTime', listen);
  player.on('property:interactiveChannel:channelState', listen);
  player.on('property:vodchannel:channelState', listen);
  player.on('property:smallMode', listen);
  player.on('player:autoQualitySwitch', listen);
  player.on('property:epg:currentChannel', listen);
  player.on('property:epg:currentProgram', listen);
  player.on('property:browserAutoplayState', listen);
  player.on('property:skin:showCloseButton', listen);
  player.on('property:sip:canSkip', listen);
  player.on('sip:skipped', listen);
  player.on('property:skin:showFavoriteButton', listen);
  player.on('property:skin:showShareButton', listen);
  player.on('property:skin:showVolumeButton', listen);
  player.on('property:skin:showEmojiButton', listen);
  player.on('property:player:remindInfo', listen);
  player.on('epg:activeChannelCurrentProgramChanged', listen);
  player.on('player:completed', listen);
  player.on('player:mediaBeforeComplete', listen);
  player.on('streamFetch', listen);
  player.on('streamFetchSilent', listen);
  player.on('streamComplete', listen);
  player.on('property:pip:isPipModeOn', listen);
  player.on('property:pip:pipIsSupported', listen);
  player.on('epg:channelSwitch', listen);
  player.on('property:cms:hideSkin', listen);
  player.on('property:playlist:info', listen);
  player.on('property:playlist:item', listen);
  player.on(
    'property:interactive:preferences',
    updateOnEvent('interactivePreferences')
  );
  player.on('property:interactive:zone', updateOnEvent('interactiveZone'));
  player.on('property:epg:currentTimestamp', listen);

  createInteractiveListeners(player, onUpdate, getState);
  player.on('property:interactive:available', listen); // TODO: Does it property still used?
  player.on('property:interactive:fullscreen', listen);
  player.on('epg2:channelClick', listen);
  player.on('property:epg2:onboarding', listen);
  player.on('player:stop', listen);

  // set initial properties' values
  state.volume = player.get('volume');
  const initialData = {
    ...getDataByBrowserAutoplay(player.get('browserAutoplayState')),
    isFavorite: player.get('player:isFavorite') || false,
    interactivePreferences: player.get('property:interactive:preferences'),
    smallMode: player.get('smallMode'),
    onboarding: {
      isEnabled: false,
      activeElement: null,
    },
  };
  // TODO: what the ... reason for this condition?
  if (initialData) {
    Object.keys(initialData).forEach((key) => {
      state[key] = initialData[key];
    });
  }

  if (screenfull.enabled) {
    screenfull.on('change', () => {
      listen({ name: 'fullscreen' });
    });
  }

  registerDerivedProp(
    'stream',
    (stream) => {
      const state = getState();
      const videoId = state.videoId;
      const types = ['tv_dvr', 'm_ch', 'vod', 'tv_linear', 'live'];
      let result = state.isFavorite;

      if (
        videoId !== stream.video_id &&
        (!stream.content_type || types.includes(stream.content_type))
      ) {
        result = get(stream, 'is_favorite', false);
      }
      return result;
    },
    'isFavorite'
  );
  registerDerivedProp('stream', (stream) => get(stream, 'title', ''), 'title');
  registerDerivedProp(
    'stream',
    (stream) => get(stream, 'watermark_clickable_enabled', false),
    'isWatermarkClickable'
  );
  registerDerivedProp(
    'stream',
    (stream) => get(stream, 'watermark', ''),
    'srcWaterMark'
  );
  registerDerivedProp(
    'stream',
    (stream) => get(stream, 'share_link', ''),
    'shareLink'
  );
  registerDerivedProp(
    'stream',
    (stream) => get(stream, 'root_video_id', null),
    'rootVideoId'
  );
  registerDerivedProp(
    'stream',
    (stream) => get(stream, 'showCloseButton', false),
    'showCloseButton'
  );
  registerDerivedProp(
    'stream',
    (stream) => (getState().channel ? getState().channel.id : stream.video_id),
    'videoId'
  );
  registerDerivedProp(
    'stream',
    (stream) => constructPrevNextObject(stream, 'next'),
    'nextObject'
  );
  registerDerivedProp(
    'stream',
    (stream) => constructPrevNextObject(stream, 'prev'),
    'prevObject'
  );

  return state;

  function listen(event) {
    let updatedData;
    const data = event.data;
    const name = normalizeEventName(event.name);

    switch (name) {
      case 'loadCalled':
        updatedData = { loadCalled: data };
        break;
      case 'videoInfo': {
        updatedData = {
          currentTime: 0,
          loadedFraction: 0,
          showMainLoader: false,
          showSkipIntroButton: false,
          skipIntroSkipped: false,
          stream: data,
          contentType: data.content_type,
          type: getTypeOfContent(data),
          ended: false,
          thumbnails: initialState.thumbnails,
          playlistInfo: null,
          playlistItem: null,
          isHybridAdSkin: data?.content_type?.indexOf('m_ch') === 0,
        };
        const prevState = getState();
        if (
          !prevState.stream ||
          prevState.stream.video_id !== data.video_id ||
          prevState.stream.virtual_id !== data.virtual_id
        ) {
          updatedData.mainContentStarted = false;
        }
        if (data.content_type === 'tv_linear') {
          updatedData.channelState = null;
        }
        break;
      }
      case 'duration':
        updatedData = { duration: Math.round(data) };
        break;
      case 'position':
        updatedData = { currentTime: data < 0 ? 0 : data };
        break;
      case 'liveDvr:position':
        updatedData = { livePosition: data };
        break;
      case 'liveDvr:duration':
        updatedData = { liveDuration: data };
        break;
      case 'liveDvr:time':
        updatedData = { liveTime: data };
        break;
      case 'liveDvr:isTimeshift':
        updatedData = { liveIsTimeshift: data };
        break;
      case 'thumbnails':
        updatedData = { thumbnails: data };
        break;
      case 'muted':
        updatedData = { muted: data };
        break;
      case 'volume':
        updatedData = { volume: data };
        break;
      case 'qualityList':
        updatedData = {
          qualityList: data,
          quality: player.get('quality'),
        };
        break;
      case 'audioTrackList':
        updatedData = {
          audioTrackList: data,
          audioTrack: player.get('audioTrack'),
        };
        break;
      case 'subtitlesList':
        updatedData = {
          subtitlesList: data,
          subtitles: player.get('subtitles'),
        };
        break;
      case 'sizeInfo':
        updatedData = { sizeInfo: data };
        break;
      case 'fullscreen':
        updatedData = { isFullscreen: screenfull.isFullscreen };
        break;
      case 'iOSFullscreen':
        updatedData = { isFullscreen: data };
        player.set('iOSFullscreen', data);
        break;
      case 'player:isFavorite':
        updatedData = {
          isFavorite: Boolean(data.state),
          isFavoritePhrase: data.tooltipPhrase,
        };
        break;
      case 'quality':
        updatedData = { quality: data };
        break;
      case 'audioTrack':
        updatedData = { audioTrack: data };
        break;
      case 'subtitles':
        updatedData = { subtitles: data };
        break;
      case 'subtitlesTextChange':
        updatedData = { subtitlesText: data };
        break;
      case 'loadedFraction':
        updatedData = { loadedFraction: data };
        break;
      case 'interactiveChannel:currentProgramDuration':
        updatedData = { programDuration: data };
        break;
      case 'interactiveChannel:currentPlayingTime':
        // this condition just for the cold start of the dvr channel
        // need to fix this in mggp-plugin-dvr if this will occur some other bugs
        updatedData = getState().program
          ? { programPosition: data < 0 ? 0 : data }
          : {};
        break;
      case 'interactiveChannel:channelState':
        updatedData = { channelState: data };
        break;
      case 'epg:currentProgramTime':
        updatedData = { currentProgramTime: data };
        break;
      case 'epg:currentChannel':
        const { isFavorite = player.get('player:isFavorite') || false } = data;
        updatedData = {
          channel: data,
          isFavorite,
          programDuration: 0,
        };
        break;
      case 'epg:currentProgram':
        updatedData = { currentProgram: data };
        break;
      case 'epg:activeChannelCurrentProgramChanged':
        updatedData = { program: data };
        break;
      case 'interactiveChannel:currentPlayingProgram': {
        const { program: prevProgram } = getState();
        updatedData = { program: data };

        if (data?.id !== prevProgram?.id) {
          updatedData.programPosition = 0;
        }

        break;
      }
      case 'ad:vendor:stateChange': {
        const { adLinear, isHybridAdSkin } = getState();
        const isHybridAdShowed = adLinear && isHybridAdSkin;
        if (!isHybridAdShowed) {
          return;
        }
        switch (data) {
          case 1:
            updatedData = {
              isBuffering: false,
              state: data,
              paused: false,
              isAbleToHideSkin: true,
            };
            break;
          case 2:
            updatedData = {
              state: data,
              paused: true,
              isAbleToHideSkin: false,
            };
            break;
        }

        break;
      }
      case 'player:stateChange': {
        const { adLinear, adPlaying, isHybridAdSkin } = getState();
        const adShowed = adLinear && adPlaying && isHybridAdSkin;
        if (adShowed) {
          return;
        }
        switch (data) {
          case 0: {
            // END
            const isTVProgram = ['onAir', 'timeshift', 'catchup'].includes(
              getState().channelState
            );
            updatedData = {
              state: data,
              currentTime: 0,
              loadedFraction: 0,
              ended: isTVProgram ? false : true,
            };
            break;
          }
          case 1: // PLAYING
            updatedData = {
              mainContentStarted: true,
              state: data,
              paused: false,
              isBuffering: false,
              started: true,
              ended: false,
              isAbleToHideSkin: true,
            };
            window.clearTimeout(pauseTimeoutIdOn5State);
            pauseTimeoutIdOn5State = undefined;
            break;
          case 2: // PAUSE
            updatedData = {
              state: data,
              paused: true,
              isBuffering: false,
              isAbleToHideSkin: false,
            };
            break;
          case 3: // BUFFER
            updatedData = {
              mainContentStarted: true,
              state: data,
              isBuffering: true,
            };
            break;
          case 4: // SEEK
            updatedData = {
              state: data,
              isBuffering: true,
            };
            break;
          case 5: // WAITING (some middle state)
            updatedData = {
              mainContentStarted: true,
              isBuffering: false,
              ended: false,
            };
            window.clearTimeout(pauseTimeoutIdOn5State);
            pauseTimeoutIdOn5State = window.setTimeout(() => {
              onUpdate({
                paused: true,
              });
            }, 300);
            break;
          default:
        }
        updatedData.state = data;
        break;
      }

      case 'player:messageMode':
        updatedData = {
          messageModeData: data,
        };
        break;

      case 'chromecastState':
        updatedData = {
          chromecastState: data,
        };
        break;

      case 'backgroundPoster':
        updatedData = {
          backgroundPoster: data,
        };
        break;

      case 'browserAutoplayState':
        updatedData = getDataByBrowserAutoplay(data);
        break;

      case 'autoplay':
        updatedData = {
          isAutoplay: data,
        };
        break;

      case 'smallMode':
        updatedData = {
          smallMode: data,
        };
        break;

      case 'player:autoQualitySwitch':
        updatedData = {
          autoQualityIndex: data,
        };
        break;

      case 'player:completed':
      case 'player:mediaBeforeComplete': {
        const isTVProgram = ['onAir', 'timeshift', 'catchup'].includes(
          getState().channelState
        );
        updatedData = {
          ended: isTVProgram ? false : true,
        };
        break;
      }

      case 'videoInstances':
        if (data && data.length > 0) {
          for (let i = 0, l = data.length; i < l; ++i) {
            if (data[i].className === 'player:video') {
              data[i].style.backgroundColor = 'black';
              data[i].addEventListener('webkitbeginfullscreen', () =>
                listen({
                  name: 'iOSFullscreen',
                  data: true,
                })
              );
              data[i].addEventListener('webkitendfullscreen', () =>
                listen({
                  name: 'iOSFullscreen',
                  data: false,
                })
              );
              break;
            }
          }
        }

        const videoPlayerInstance = data.find((item) =>
          item.classList.contains('player:video')
        );

        updatedData = {
          videoInstances: data,
          videoPlayerInstance,
        };
        break;

      case 'skin:showCloseButton':
        updatedData = {
          showCloseButton: data,
        };
        break;

      case 'sip:canSkip':
        updatedData = {
          showSkipIntroButton: data,
          skipIntroSkipped: false,
        };
        break;

      case 'sip:skipped':
        updatedData = {
          showSkipIntroButton: false,
          skipIntroSkipped: true,
        };
        break;

      case 'skin:showFavoriteButton':
        updatedData = {
          showFavoriteButton: data,
        };
        break;

      case 'skin:showShareButton':
        updatedData = {
          showShareButton: data,
        };
        break;

      case 'skin:showVolumeButton':
        updatedData = {
          showVolumeButton: data,
        };
        break;

      case 'skin:showEmojiButton':
        updatedData = {
          showEmojiButton: data,
        };
        break;
      case 'streamFetch':
        updatedData = {
          showMainLoader: true,
          disableLoaders: false,
        };
        break;

      case 'streamFetchSilent':
        updatedData = {
          disableLoaders: data,
        };
        break;

      case 'streamComplete':
        updatedData = {
          showMainLoader: false,
          disableLoaders: false,
        };
        break;

      case 'player:remindInfo':
        updatedData = {
          remindInfo: data,
        };
        break;

      case 'pip:pipIsSupported':
        updatedData = {
          pipIsSupported: data,
        };
        break;

      case 'pip:isPipModeOn':
        updatedData = {
          isPipModeOn: data,
        };
        break;

      case 'epg:channelSwitch': {
        const currentState = getState();
        if (
          !currentState.isAutoplay &&
          currentState.browserAutoplayState === BROWSER_AUTOPLAY_STATE.AVAILABLE
        ) {
          updatedData = {
            isAutoplay: true,
          };
        }
        break;
      }

      case 'interactive:fullscreen': {
        updatedData = {
          isInteractiveFullscreen: data,
        };

        break;
      }

      case 'cms:hideSkin': {
        updatedData = {
          cmsWantsToHideSkin: data,
        };
        break;
      }

      case 'playlist:info': {
        updatedData = { playlistInfo: data };
        break;
      }

      case 'playlist:item': {
        updatedData = { playlistItem: data };
        break;
      }

      case 'epg:currentTimestamp': {
        updatedData = { epgCurrentTimestamp: data };
        break;
      }

      case 'epg2:channelClick': {
        updatedData = { showMainLoader: true };
        break;
      }

      case 'epg2:onboarding': {
        updatedData = {
          onboarding: {
            isEnabled: Boolean(data),
            activeElement: data?.activeElement || null,
          },
        };
        break;
      }

      case 'player:stop': {
        if (getState().contentType === 'trailer') {
          updatedData = {
            contentType: initialState.contentType,
          };
        }
        break;
      }

      default:
        updatedData = {
          [name]: data,
        };
    }

    updatedData = {
      ...updatedData,
      ...calculateDerivedProps(updatedData, getState()),
    };

    onUpdate(updatedData);
  }
};

function constructPrevNextObject(obj, prevNext) {
  let result = null;
  if (obj[prevNext]) {
    result = {
      id: obj[prevNext],
      title:
        obj.siblings && obj.siblings[prevNext]
          ? constructSiblingTitle(obj.siblings[prevNext].titles)
          : obj.title,
      image: get(obj.images, 'small', null),
    };
  }
  return result;
}

function getDataByBrowserAutoplay(data) {
  let updatedData = {
    browserAutoplayState: data,
  };
  if (data === BROWSER_AUTOPLAY_STATE.NEED_TO_INIT_VIDEOS) {
    updatedData.isAutoplay = false;
    updatedData.showMainLoader = false;
  } else if (data === BROWSER_AUTOPLAY_STATE.AVAILABLE) {
    updatedData.isAutoplay = true;
  }
  return updatedData;
}

function constructSiblingTitle(titles) {
  return titles ? titles.original : '';
}
